import type { MerchantApiClient } from "../../types.js";
import { dispatchMerchantTelemetry } from "../../shared/telemetry.js";
import { createAnalyticsApi, type AnalyticsApi } from "./api.js";
import {
  deriveAnalyticsMetrics,
  type AnalyticsFilters,
  type AnalyticsViewModel,
} from "./models.js";

export interface AnalyticsTelemetry {
  track(eventName: string, payload: Record<string, string | number | boolean | undefined>): void;
}

export interface AnalyticsService {
  loadAnalytics(filters: AnalyticsFilters): Promise<AnalyticsViewModel>;
}

export interface AnalyticsServiceOptions {
  api?: AnalyticsApi;
  apiClient?: MerchantApiClient;
  telemetry?: AnalyticsTelemetry;
}

export function createAnalyticsService(options: AnalyticsServiceOptions): AnalyticsService {
  const api = options.api ?? createApiFromClient(options.apiClient);
  const telemetry = options.telemetry ?? browserTelemetry();
  return {
    async loadAnalytics(filters) {
      return track(
        telemetry,
        "merchant_analytics_loaded",
        async () => {
          const [dashboard, timeseries] = await Promise.all([
            api.getDashboard(filters),
            api.getTimeSeries(filters),
          ]);
          return { dashboard, timeseries, derived: deriveAnalyticsMetrics(dashboard) };
        },
        { fromDay: filters.fromDay, toDay: filters.toDay, currency: filters.currency },
      );
    },
  };
}

async function track<T>(
  telemetry: AnalyticsTelemetry,
  eventName: string,
  operation: () => Promise<T>,
  payload: Record<string, string | number | boolean | undefined>,
): Promise<T> {
  const startedAt = performance.now();
  try {
    const result = await operation();
    telemetry.track(eventName, {
      ...payload,
      result: "success",
      durationMs: Math.round(performance.now() - startedAt),
    });
    return result;
  } catch (error: unknown) {
    telemetry.track(eventName, {
      ...payload,
      result: "failure",
      durationMs: Math.round(performance.now() - startedAt),
      errorName: error instanceof Error ? error.name : "unknown",
    });
    throw error;
  }
}

function createApiFromClient(client: MerchantApiClient | undefined): AnalyticsApi {
  if (client === undefined) throw new Error("Analytics service requires an API client.");
  return createAnalyticsApi(client);
}

function browserTelemetry(): AnalyticsTelemetry {
  return {
    track(eventName, payload) {
      dispatchMerchantTelemetry(eventName, payload);
    },
  };
}
